import { Fragment } from 'react'
import ReactMarkdown from 'react-markdown'
import { Box } from '~/design_system'
import { formatDateTime } from '~/common/tzUtils'
import { useAccountMeQuery } from '~/api/accounts'
import { renderLogMessage } from '~/activity_log/activity_log'
import { Msg } from '~/common/localizationUtils'
import { NoResultsMessage } from './NoResultsMessage'
import { SadStates } from './SadStates'
import styles from './ActivityLog.module.scss'

const ApiNote = () => (
  <div data-testid="log_item_api_note" className={styles.ActivityLog__footnote}>
    <em>
      <Msg id="activity_log.api_source.note" />
    </em>
  </div>
)

const McpNote = () => (
  <div data-testid="log_item_mcp_note" className={styles.ActivityLog__footnote}>
    <em>
      <Msg id="activity_log.mcp_source.note" />
    </em>
  </div>
)

const UserNote = ({ userFullName, impersonatorName }) =>
  impersonatorName ? (
    <div data-testid="log_item_user" className={styles.ActivityLog__footnote}>
      <em>
        <Msg
          id="activity_log.action_completed_by"
          description=" - Action completed by: {impersonator_name} on behalf of {user_full_name}"
          impersonator_name={impersonatorName}
          user_full_name={userFullName}
        />
      </em>
    </div>
  ) : (
    <div data-testid="log_item_user" className={styles.ActivityLog__footnote}>
      <em>
        <Msg
          id="activity_log.action_completed_by_full_name"
          description=" - Action completed by: {user_full_name}"
          user_full_name={userFullName}
        />
      </em>
    </div>
  )

/**
 * Instead of going through and trying to retrofix all embedded HTML in activity log, just make this component
 * pattern match escaped tags that we care to support and convert to markdown when rendering
 * Keep tag list in sync with PLAIN_TEXT_ALLOWED_TAGS in app/activity_log/render.py
 * @param msg
 * @returns {*}
 */
export const markdownEscapedTags = msg =>
  msg.replace(
    /(<|&lt;)\/?(b|br|em|i|strong)(>|&gt;)/g,
    (m, g1, g2) =>
      ({
        // Markdown escapes for common tags
        b: '**',
        br: '\n\n',
        em: '*',
        i: '*',
        strong: '**'
      })[g2]
  )

export const ActivityLog = ({
  activityLogs,
  oneColumn = true,
  separator = true,
  maxHeight = 110,
  title = <Msg id="term.activity_log" description="Activity Log" />,
  noLogsMessage = <Msg id="activity_log.no_activity_log_data" description="No Activity Log Data is Available" />
}) => {
  const hasActivityLogs = activityLogs && activityLogs.length
  const { data: { timezone, locale } = {} } = useAccountMeQuery()

  return (
    <div className={styles.ActivityLog} data-testid="activity_log">
      <h4 className={styles.ActivityLog__header}>{title}</h4>
      <Box className={styles.ActivityLog__scrollbody} maxH={maxHeight}>
        <table className={styles.ActivityLog__list}>
          <tbody>
            <SadStates
              states={[
                {
                  when: !hasActivityLogs,
                  render: () => (
                    <tr>
                      <td>
                        <NoResultsMessage style={{ margin: 10 }} message={noLogsMessage} />
                      </td>
                    </tr>
                  )
                }
              ]}>
              {hasActivityLogs &&
                activityLogs.map(a => (
                  <Fragment key={a.log_id}>
                    {oneColumn ? (
                      <tr data-testid="items-list-item">
                        <td>
                          {a.header && <b>{a.header}</b>}
                          <div data-testid="log_item_message">
                            <ReactMarkdown components={{ p: 'div' }}>
                              {markdownEscapedTags(renderLogMessage(a))}
                            </ReactMarkdown>
                          </div>
                          {a.link && (
                            <a href={a.link} className={styles.ActivityLog__link}>
                              {a.link_text}
                            </a>
                          )}
                          <UserNote userFullName={a.user_full_name} impersonatorName={a.impersonator_name} />
                          {a.mcp_source ? <McpNote /> : a.api_source && <ApiNote />}
                          <div data-testid="log_item_date" className={styles.ActivityLog__oneColumn__timestamp}>
                            <b>{formatDateTime(a.date, timezone, locale)}</b>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        {a.header && (
                          <td>
                            <b>{a.header}</b>
                          </td>
                        )}
                        <td>
                          <ReactMarkdown components={{ p: 'div' }}>
                            {markdownEscapedTags(renderLogMessage(a))}
                          </ReactMarkdown>
                          {a.link && (
                            <a href={a.link} className={styles.ActivityLog__link}>
                              {a.link_text}
                            </a>
                          )}
                          <UserNote userFullName={a.user_full_name} impersonatorName={a.impersonator_name} />
                          {a.mcp_source ? <McpNote /> : a.api_source && <ApiNote />}
                        </td>
                        <td className={styles.ActivityLog__timestamp}>
                          <b>{formatDateTime(a.date, timezone)}</b>
                        </td>
                      </tr>
                    )}
                    {separator ? <tr className={styles.ActivityLog_separator} /> : null}
                  </Fragment>
                ))}
            </SadStates>
          </tbody>
        </table>
      </Box>
    </div>
  )
}
